import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { terrainHeightAt } from "./terrain";
const HIT_FLASH_DURATION = 0.12;
const HITS_TO_DESTROY = 4;

export type TargetAssetSpec = {
  path: string;
  scale: number;
  rotationOffset?: number;
};

type TargetState = {
  object: THREE.Object3D;
  mats: THREE.MeshStandardMaterial[];
  hitsLeft: number;
  flashLife: number;
  alive: boolean;
};

export class TargetRange {
  readonly group = new THREE.Group();
  private readonly targets: TargetState[] = [];

  constructor(
    count = 8,
    positions?: Array<[number, number]>,
    assets?: AssetPipeline,
    assetSpecs: TargetAssetSpec[] = [],
  ) {
    const targetPositions: Array<[number, number]> = positions ?? [
      [80, 120], [-150, 90], [200, -80], [-60, 200],
      [300, 50], [-200, -150], [120, -220], [-300, 100],
    ].slice(0, count) as Array<[number, number]>;

    for (const [idx, [x, z]] of targetPositions.entries()) {
      const mat = this.createTargetMaterial();
      const object = new THREE.Group();
      object.position.set(x, terrainHeightAt(x, z), z);
      const rotation = Math.atan2(x, z) + Math.PI * 0.25;
      object.rotation.y = rotation;
      this.group.add(object);
      this.targets.push({ object, mats: [], hitsLeft: HITS_TO_DESTROY, flashLife: 0, alive: true });

      const targetRef = this.targets.length - 1;
      const spec = assetSpecs[idx % assetSpecs.length];
      if (assets && spec) {
        void this.loadAssetTarget(assets, spec, object, targetRef);
      } else {
        const legacy = this.buildLegacyTarget(mat);
        legacy.traverse((child) => {
          child.userData["isTarget"] = true;
          child.userData["targetRef"] = targetRef;
        });
        object.add(legacy);
        this.targets[targetRef]!.mats = [mat];
      }
    }
  }

  private createTargetMaterial(): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color: 0x5a3d25,
      roughness: 0.68,
      metalness: 0.12,
      emissive: new THREE.Color(0x220600),
      emissiveIntensity: 0.35,
    });
  }

  private buildLegacyTarget(mat: THREE.MeshStandardMaterial): THREE.Object3D {
    const body = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.2, 2.4, 8), mat);
    body.position.y = 1.2;
    return body;
  }

  private async loadAssetTarget(
    assets: AssetPipeline,
    spec: TargetAssetSpec,
    parent: THREE.Object3D,
    targetRef: number,
  ): Promise<void> {
    const template = await assets.loadSceneTemplate(spec.path);
    const clone = assets.cloneSceneTemplate(template);
    clone.scale.setScalar(spec.scale);
    clone.rotation.y = spec.rotationOffset ?? 0;
    const mats: THREE.MeshStandardMaterial[] = [];
    clone.traverse((child) => {
      child.userData["isTarget"] = true;
      child.userData["targetRef"] = targetRef;
      if (child instanceof THREE.Mesh || child instanceof THREE.SkinnedMesh) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        for (const material of materials) {
          if (material instanceof THREE.MeshStandardMaterial) {
            material.emissive = new THREE.Color(0x220600);
            material.emissiveIntensity = 0.45;
            mats.push(material);
          }
        }
      }
    });
    parent.add(clone);
    const target = this.targets[targetRef];
    if (target) target.mats = mats;
  }

  hitInRadius(center: THREE.Vector3, radius: number): void {
    for (const t of this.targets) {
      if (!t.alive) continue;
      if (t.object.position.distanceTo(center) <= radius) this.hitTarget(t);
    }
  }

  hit(object: THREE.Object3D): void {
    if (!object.userData["isTarget"]) return;
    const idx = object.userData["targetRef"] as number;
    const t = this.targets[idx];
    if (!t || !t.alive) return;
    this.hitTarget(t);
  }

  private hitTarget(t: TargetState): void {
      t.hitsLeft -= 1;
      t.flashLife = HIT_FLASH_DURATION;
      if (t.hitsLeft <= 0) {
        t.alive = false;
      t.object.visible = false;
    }
  }

  update(dt: number): void {
    for (const t of this.targets) {
      if (t.flashLife > 0) {
        t.flashLife -= dt;
        const bright = t.flashLife > 0;
        for (const mat of t.mats) {
          mat.emissive.set(bright ? 0xffffff : 0x220600);
          mat.emissiveIntensity = bright ? (t.flashLife / HIT_FLASH_DURATION) * 1.8 : 0.45;
        }
      }
    }
  }

  get aliveCount(): number {
    return this.targets.filter((t) => t.alive).length;
  }
}
